import { FC, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useField, useForm } from 'react-final-form';
import { useSelector } from 'react-redux';

import { BottomSheet } from '@beeline/design-system-react/extracted-styles';
import { BottomSheetActionsType } from '@beeline/design-system-react/types/components/BottomSheet/BottomSheet.types';
import { Icons } from '@beeline/design-tokens/js/iconfont';

import { useHost } from 'src/providers/HostProvider';
import { getAdminState } from 'src/store/admin/selectors';
import { adminActions } from 'src/store/admin/slice';
import { removeTown } from 'src/store/towns/services';
import { useAppDispatch } from 'src/store/useAppDispatch';

import { AdministratorTownFields } from '../AdministratorTownForm/AdministratorTownFields';
import { AdministratorTownForm } from '../AdministratorTownForm/AdministratorTownForm';

import classes from './TownBottomSheet.module.scss';

const Fields: FC = () => {
  const id = useField<number | undefined>('id').input.value;
  const dispatch = useAppDispatch();

  const { submit, reset } = useForm();

  const { townIsOpened } = useSelector(getAdminState);

  const close = useCallback(() => {
    dispatch(adminActions.closeTown());
  }, [dispatch]);

  const saveTown = useCallback(async () => {
    const result = await submit();
    if (!result) throw new Error('Form is invalid');

    close();
    reset();
  }, [close, reset, submit]);

  const deleteTown = useCallback(async () => {
    if (id) await dispatch(removeTown(id));

    close();
  }, [close, dispatch, id]);

  const actions = useMemo((): BottomSheetActionsType | undefined => {
    const result: BottomSheetActionsType = [
      {
        label: id ? 'Сохранить' : 'Создать',
        variant: 'contained',
        onClick: saveTown,
      },
    ];
    if (id)
      result.unshift({
        label: 'Удалить',
        variant: 'outlined',
        onClick: deleteTown,
      });

    return result;
  }, [deleteTown, id, saveTown]);

  const handleSetOpen = useCallback(
    (value: boolean) => {
      dispatch(adminActions.setTownIsOpened(value));
      reset();
    },
    [dispatch, reset],
  );

  return (
    <BottomSheet
      isOpen={townIsOpened}
      handleSetOpen={handleSetOpen}
      swiper
      modal={false}
      snapPoints={[1]}
      className={classes.settings}
      initialSnapPoint={0}
      header={{
        icon: Icons.City,
        title: id ? 'Редактирование города' : 'Создание города',
        closeButton: true,
      }}
      actions={actions}
    >
      <div className={classes.container}>
        <AdministratorTownFields />
      </div>
    </BottomSheet>
  );
};

const FormBottomSheet: FC = () => {
  return (
    <AdministratorTownForm>
      <Fields />
    </AdministratorTownForm>
  );
};

export const TownBottomSheet: FC = () => {
  const { host } = useHost();

  if (!host) return null;

  return createPortal(<FormBottomSheet />, host);
};
